/**
 * The `#/dev/effects` comparer (W3-04 effects-fidelity; WORKPACKAGES Appendix C H5): Phiedit's preview of one
 * extra.json effect side by side with Phira's screenshot of the same frame.
 *
 * Workflow: pick an effect and its values, download the capture kit (captureKit.ts) and play it in Phira, take one
 * screenshot before 0:16 ("Before": effect off) and one after ("Phira": effect on), and load both. The page runs the
 * effect over Before with the preview's own runner (EffectBench) and compares the result with the Phira screenshot:
 * side by side, blinking, or as a difference map with numbers. "Matches" / "Differs" writes a comparison record
 * (signOffRecord.ts); a matching built-in is signed off by adding its entry to builtin/signOff.ts, which removes its
 * "approximate" badge. Without screenshots the page still shows the effect over the built-in test card.
 *
 * Framework-free DOM (the render layer has no React): `mountEffectsComparer` builds the page into a host element and
 * returns the function that removes it. Every string comes through `t` (namespaces `effectsDev`, `builtinFx`);
 * colours come from the design tokens (CSS module; the heat map reads two tokens).
 */

import { BUILTIN_SHADER_NAMES } from '../../../../core/model/schema';
import { m as fx } from '../builtin/builtinFx.messages';
import { APPROXIMATE_BADGE, BUILTIN_EFFECTS, builtinShaderFidelity, type BuiltinUniform } from '../builtin/index';
import { EffectBench, letterbox, type BenchShader, type RgbaImage } from './bench';
import { buildCaptureKit, KIT_TIMES } from './captureKit';
import { DEFAULT_THRESHOLD, diffImages, parseCssRgb, type DiffStats, type Rgb } from './compare';
import { m } from './effectsDev.messages';
import { encodePng } from './encode';
import { UV_PROBE_SOURCE, UV_PROBE_UNIFORMS } from './probe';
import { readsTime, signOffEntry, signOffFileName, signOffJson, signOffRecord, type ComparedEffect } from './signOffRecord';
import { CARD_PREVIEW, testCard } from './testCard';
import styles from './comparerPage.module.css';

const CLASS_NAMES = [
  'badge',
  'badgeVerified',
  'button',
  'buttons',
  'canvas',
  'card',
  'cardTitle',
  'color',
  'column',
  'controls',
  'drop',
  'dropActive',
  'dropName',
  'empty',
  'error',
  'header',
  'hidden',
  'hint',
  'intro',
  'json',
  'label',
  'layout',
  'notice',
  'number',
  'page',
  'pane',
  'paneTitle',
  'panes',
  'primary',
  'range',
  'row',
  'segment',
  'segmented',
  'select',
  'stack',
  'stat',
  'stats',
  'text',
  'textarea',
  'title',
] as const;
type ClassName = (typeof CLASS_NAMES)[number];
/** The CSS module's class names (the plain name when the module gives none, as in unit tests). */
const s = Object.fromEntries(CLASS_NAMES.map((k) => [k, styles[k] ?? k])) as Readonly<Record<ClassName, string>>;

export type Translate = (key: string, params?: Readonly<Record<string, string | number>>) => string;

export interface ComparerDeps {
  readonly t: Translate;
  /** Creates the WebGL2 bench (tests replace it); null = WebGL2 unavailable. */
  readonly createBench?: () => EffectBench | null;
}

type UniformValue = number | readonly number[];
type View = 'side' | 'blink' | 'diff';
type Scope = 'global' | 'chart';

interface Picture {
  readonly image: RgbaImage;
  /** File name, or null for the test card. */
  readonly name: string | null;
}

const EFFECTS: readonly ComparedEffect[] = [...BUILTIN_SHADER_NAMES, 'uvProbe'];
const CHART_ASPECT = 16 / 9;
const BLINK_MS = 700;

export function uniformsOf(effect: ComparedEffect): readonly BuiltinUniform[] {
  return effect === 'uvProbe' ? UV_PROBE_UNIFORMS : BUILTIN_EFFECTS[effect].uniforms;
}

export function shaderOf(effect: ComparedEffect): BenchShader {
  return effect === 'uvProbe' ? { kind: 'custom', source: UV_PROBE_SOURCE } : { kind: 'builtin', name: effect };
}

function defaultsOf(effect: ComparedEffect): Map<string, UniformValue> {
  return new Map(uniformsOf(effect).map((u) => [u.name, typeof u.default === 'number' ? u.default : [...u.default]]));
}

type Attrs = Readonly<Record<string, string | number | boolean | undefined>>;

function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  ...children: ReadonlyArray<Node | string | null>
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k === 'class') e.className = String(v);
    else e.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children) if (c !== null) e.append(c);
  return e;
}

const hex2 = (v: number): string =>
  Math.round(Math.min(1, Math.max(0, v)) * 255)
    .toString(16)
    .padStart(2, '0');

function toHex(c: readonly number[]): string {
  return `#${hex2(c[0] ?? 0)}${hex2(c[1] ?? 0)}${hex2(c[2] ?? 0)}`;
}

function fromHex(hex: string): [number, number, number] {
  const rgb = parseCssRgb(hex) ?? [0, 0, 0];
  return [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];
}

/** Draws an image into a canvas (resizing the canvas to it). */
function paint(canvas: HTMLCanvasElement, img: RgbaImage): void {
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return;
  const data = ctx.createImageData(img.width, img.height);
  data.data.set(img.data);
  ctx.putImageData(data, 0, 0);
}

function canvas2d(w: number, h: number): OffscreenCanvasRenderingContext2D | null {
  return new OffscreenCanvas(Math.max(1, w), Math.max(1, h)).getContext('2d');
}

/** Reads an image file (PNG, JPEG, WebP, ...) as RGBA pixels. */
async function readImageFile(file: Blob): Promise<RgbaImage> {
  const bmp = await createImageBitmap(file);
  try {
    const ctx = canvas2d(bmp.width, bmp.height);
    if (ctx === null) throw new Error('no 2d context');
    ctx.drawImage(bmp, 0, 0);
    const d = ctx.getImageData(0, 0, bmp.width, bmp.height);
    return { width: d.width, height: d.height, data: d.data };
  } finally {
    bmp.close();
  }
}

/** The image scaled to w x h (smooth). */
function scaleImage(img: RgbaImage, w: number, h: number): RgbaImage {
  const src = canvas2d(img.width, img.height);
  const dst = canvas2d(w, h);
  if (src === null || dst === null) return img;
  const data = src.createImageData(img.width, img.height);
  data.data.set(img.data);
  src.putImageData(data, 0, 0);
  dst.imageSmoothingQuality = 'high';
  dst.drawImage(src.canvas, 0, 0, w, h);
  const out = dst.getImageData(0, 0, w, h);
  return { width: w, height: h, data: out.data };
}

function download(bytes: Uint8Array | string, name: string, type: string): void {
  const blob = new Blob([typeof bytes === 'string' ? bytes : new Uint8Array(bytes)], { type });
  const url = URL.createObjectURL(blob);
  const a = h('a', { href: url, download: name, class: s.hidden });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10_000);
}

/** The two heat-map colours from the design tokens (warning = just over the threshold, danger = large). */
function heatColors(host: HTMLElement): { low: Rgb; high: Rgb } {
  const style = getComputedStyle(host);
  return {
    low: parseCssRgb(style.getPropertyValue('--color-warning')) ?? [255, 255, 0],
    high: parseCssRgb(style.getPropertyValue('--color-danger')) ?? [255, 0, 0],
  };
}

/** Builds the comparer into `host`; returns the function that removes it. */
export function mountEffectsComparer(host: HTMLElement, deps: ComparerDeps): () => void {
  const { t } = deps;
  const bench = (deps.createBench ?? (() => EffectBench.create(document.createElement('canvas'))))();
  const timers = new Set<ReturnType<typeof setTimeout>>();
  let blinkTimer: ReturnType<typeof setInterval> | null = null;
  let frame = 0;
  let disposed = false;

  const state = {
    effect: 'shockwave' as ComparedEffect,
    values: defaultsOf('shockwave'),
    time: 0,
    scope: 'global' as Scope,
    view: 'side' as View,
    before: { image: testCard(CARD_PREVIEW.w, CARD_PREVIEW.h), name: null } as Picture,
    beforeDirty: true,
    phira: null as Picture | null,
    ours: null as RgbaImage | null,
    stats: null as DiffStats | null,
    verdict: null as 'matches' | 'differs' | null,
    blinkOurs: true,
  };

  const labelOf = (e: ComparedEffect): string =>
    e === 'uvProbe' ? t(m.probe) : t(BUILTIN_EFFECTS[e].label.key, BUILTIN_EFFECTS[e].label.params);

  // ------------------------------------------------------------------ effect card
  const effectSelect = h('select', { id: 'fx-effect', class: s.select });
  for (const e of EFFECTS) effectSelect.append(h('option', { value: e }, labelOf(e)));
  effectSelect.value = state.effect;
  const effectDesc = h('p', { class: s.hint });
  const badge = h('span', { class: s.badge, role: 'note' });
  const valuesBox = h('div', { class: s.column });
  const timeInput = h('input', {
    id: 'fx-time',
    class: s.number,
    type: 'number',
    step: '0.1',
    value: '0',
    'aria-describedby': 'fx-time-hint',
  });
  const timeHint = h('p', { class: s.hint, id: 'fx-time-hint' });
  const scopeButtons = new Map<Scope, HTMLButtonElement>();
  const scopeGroup = h('div', { class: s.segmented, role: 'group', 'aria-labelledby': 'fx-scope-label' });
  const scopeHud = h('p', { class: s.notice, id: 'fx-scope-hud' }, t(m.scopeChartHud));
  for (const [scope, key] of [
    ['global', m.scopeGlobal],
    ['chart', m.scopeChart],
  ] as const) {
    const b = h('button', { type: 'button', class: s.segment }, t(key));
    b.addEventListener('click', () => {
      state.scope = scope;
      syncScope();
      invalidate();
    });
    scopeButtons.set(scope, b);
    scopeGroup.append(b);
  }
  const resetButton = h('button', { type: 'button', class: s.button }, t(m.resetValues));
  const effectCard = h(
    'section',
    { class: s.card, 'aria-labelledby': 'fx-effect-title' },
    h('h2', { class: s.cardTitle, id: 'fx-effect-title' }, t(m.effect)),
    effectSelect,
    effectDesc,
    badge,
    h('h3', { class: s.label }, t(m.values)),
    valuesBox,
    h('div', { class: s.row }, h('label', { class: s.label, for: 'fx-time' }, t(m.time)), timeInput),
    timeHint,
    h('div', { class: s.stack }, h('span', { class: s.label, id: 'fx-scope-label' }, t(m.scope)), scopeGroup),
    scopeHud,
    h('div', { class: s.buttons }, resetButton),
  );

  // ------------------------------------------------------------------ capture kit card
  const kitButton = h('button', { type: 'button', class: `${s.button} ${s.primary}` }, t(m.downloadKit));
  const kitCard = h(
    'section',
    { class: s.card, 'aria-labelledby': 'fx-kit-title' },
    h('h2', { class: s.cardTitle, id: 'fx-kit-title' }, t(m.kit)),
    h('p', { class: s.hint }, t(m.kitHint)),
    h('div', { class: s.buttons }, kitButton),
  );

  // ------------------------------------------------------------------ screenshots card
  function dropZone(
    id: string,
    titleKey: string,
    hintKey: string,
    onImage: (p: Picture) => void,
    extra: HTMLButtonElement,
  ): { root: HTMLElement; name: HTMLElement; error: HTMLElement } {
    const file = h('input', { id, type: 'file', accept: 'image/*', class: s.hidden });
    const choose = h('button', { type: 'button', class: s.button }, t(m.chooseImage));
    const name = h('span', { class: s.dropName });
    const error = h('p', { class: `${s.notice} ${s.error} ${s.hidden}`, role: 'alert' }, t(m.imageFailed));
    const root = h(
      'div',
      { class: s.drop },
      h('label', { class: s.label, for: id }, t(titleKey)),
      h('p', { class: s.hint }, t(hintKey)),
      h('div', { class: s.buttons }, choose, extra),
      h('span', { class: s.hint }, t(m.dropHint)),
      name,
      error,
      file,
    );
    const take = async (f: File | undefined): Promise<void> => {
      if (f === undefined) return;
      try {
        const image = await readImageFile(f);
        if (disposed) return;
        error.classList.add(s.hidden);
        onImage({ image, name: f.name });
      } catch {
        error.classList.remove(s.hidden);
      }
    };
    choose.addEventListener('click', () => {
      file.click();
    });
    file.addEventListener('change', () => {
      void take(file.files?.[0]);
      file.value = '';
    });
    root.addEventListener('dragover', (ev) => {
      ev.preventDefault();
      root.classList.add(s.dropActive);
    });
    root.addEventListener('dragleave', () => {
      root.classList.remove(s.dropActive);
    });
    root.addEventListener('drop', (ev) => {
      ev.preventDefault();
      root.classList.remove(s.dropActive);
      void take(ev.dataTransfer?.files[0]);
    });
    return { root, name, error };
  }

  const useCard = h('button', { type: 'button', class: s.button }, t(m.useTestCard));
  const removePhira = h('button', { type: 'button', class: s.button }, t(m.removeImage));
  const beforeZone = dropZone(
    'fx-before',
    m.before,
    m.beforeHint,
    (p) => {
      state.before = p;
      state.beforeDirty = true;
      invalidate();
    },
    useCard,
  );
  const phiraZone = dropZone(
    'fx-phira',
    m.phira,
    m.phiraHint,
    (p) => {
      state.phira = p;
      invalidate();
    },
    removePhira,
  );
  useCard.addEventListener('click', () => {
    state.before = { image: testCard(CARD_PREVIEW.w, CARD_PREVIEW.h), name: null };
    state.beforeDirty = true;
    invalidate();
  });
  removePhira.addEventListener('click', () => {
    state.phira = null;
    invalidate();
  });
  const imagesCard = h(
    'section',
    { class: s.card, 'aria-labelledby': 'fx-images-title' },
    h('h2', { class: s.cardTitle, id: 'fx-images-title' }, t(m.images)),
    beforeZone.root,
    phiraZone.root,
  );

  // ------------------------------------------------------------------ result card
  const viewButtons = new Map<View, HTMLButtonElement>();
  const viewGroup = h('div', { class: s.segmented, role: 'group', 'aria-label': t(m.view) });
  for (const [view, key] of [
    ['side', m.viewSide],
    ['blink', m.viewBlink],
    ['diff', m.viewDiff],
  ] as const) {
    const b = h('button', { type: 'button', class: s.segment }, t(key));
    b.addEventListener('click', () => {
      state.view = view;
      syncView();
    });
    viewButtons.set(view, b);
    viewGroup.append(b);
  }
  const notices = h('div', { class: s.column, 'aria-live': 'polite' });
  const oursCanvas = h('canvas', { class: s.canvas, 'aria-label': t(m.paneOurs), role: 'img' });
  const phiraCanvas = h('canvas', { class: s.canvas, 'aria-label': t(m.panePhira), role: 'img' });
  const diffCanvas = h('canvas', { class: s.canvas, 'aria-label': t(m.paneDiff), role: 'img' });
  const blinkCanvas = h('canvas', { class: s.canvas, role: 'img' });
  const phiraEmpty = h('div', { class: s.empty }, t(m.noCapture));
  const diffEmpty = h('div', { class: s.empty }, t(m.noCapture));
  const oursPane = h('figure', { class: s.pane }, h('figcaption', { class: s.paneTitle }, t(m.paneOurs)), oursCanvas);
  const phiraPane = h(
    'figure',
    { class: s.pane },
    h('figcaption', { class: s.paneTitle }, t(m.panePhira)),
    phiraCanvas,
    phiraEmpty,
  );
  const diffPane = h(
    'figure',
    { class: s.pane },
    h('figcaption', { class: s.paneTitle }, t(m.paneDiff)),
    diffCanvas,
    diffEmpty,
  );
  const blinkCaption = h('figcaption', { class: s.paneTitle });
  const blinkPane = h('figure', { class: s.pane }, blinkCaption, blinkCanvas);
  const panes = h('div', { class: s.panes }, oursPane, phiraPane, diffPane, blinkPane);
  const statValues = {
    mean: h('dd'),
    psnr: h('dd'),
    over: h('dd'),
    max: h('dd'),
  };
  const statsList = h(
    'dl',
    { class: s.stats },
    h('div', { class: s.stat }, h('dt', {}, t(m.statMean)), statValues.mean),
    h('div', { class: s.stat }, h('dt', {}, t(m.statPsnr)), statValues.psnr),
    h('div', { class: s.stat }, h('dt', {}, t(m.statOver, { threshold: DEFAULT_THRESHOLD })), statValues.over),
    h('div', { class: s.stat }, h('dt', {}, t(m.statMax)), statValues.max),
  );
  const saveRender = h('button', { type: 'button', class: s.button }, t(m.saveRender));
  const resultCard = h(
    'section',
    { class: s.card, 'aria-labelledby': 'fx-result-title' },
    h('h2', { class: s.cardTitle, id: 'fx-result-title' }, t(m.stats)),
    h('div', { class: s.row }, h('span', { class: s.label }, t(m.view)), viewGroup),
    notices,
    panes,
    statsList,
    h('div', { class: s.buttons }, saveRender),
  );

  // ------------------------------------------------------------------ record card
  const tester = h('input', { id: 'fx-tester', class: s.text, type: 'text', autocomplete: 'name' });
  const phiraVersion = h('input', { id: 'fx-phira-version', class: s.text, type: 'text' });
  const notes = h('textarea', { id: 'fx-notes', class: s.textarea, rows: 3 });
  const matches = h('button', { type: 'button', class: s.segment }, t(m.matches));
  const differs = h('button', { type: 'button', class: s.segment }, t(m.differs));
  const verdictGroup = h('div', { class: s.segmented, role: 'group', 'aria-label': t(m.record) }, matches, differs);
  const recordJson = h('pre', { class: s.json, tabindex: 0 });
  const entryJson = h('pre', { class: s.json, tabindex: 0 });
  const entryTitle = h('h3', { class: s.label }, t(m.signOffEntry));
  const entryHint = h('p', { class: `${s.hint} ${s.hidden}`, id: 'fx-entry-hint', role: 'status' });
  const copyButton = h('button', { type: 'button', class: s.button }, t(m.copy));
  const jsonButton = h('button', { type: 'button', class: s.button }, t(m.downloadJson));
  const recordOut = h(
    'div',
    { class: `${s.column} ${s.hidden}` },
    h('p', { class: s.hint }, t(m.recordHint)),
    recordJson,
    entryTitle,
    entryHint,
    entryJson,
    h('div', { class: s.buttons }, copyButton, jsonButton),
  );
  const recordCard = h(
    'section',
    { class: s.card, 'aria-labelledby': 'fx-record-title' },
    h('h2', { class: s.cardTitle, id: 'fx-record-title' }, t(m.record)),
    h('div', { class: s.row }, h('label', { class: s.label, for: 'fx-tester' }, t(m.tester)), tester),
    h(
      'div',
      { class: s.row },
      h('label', { class: s.label, for: 'fx-phira-version' }, t(m.phiraVersion)),
      phiraVersion,
    ),
    h('div', { class: s.row }, h('label', { class: s.label, for: 'fx-notes' }, t(m.notes)), notes),
    verdictGroup,
    recordOut,
  );

  // ------------------------------------------------------------------ page
  const page = h(
    'main',
    { class: s.page, 'data-testid': 'effects-comparer' },
    h('header', { class: s.header }, h('h1', { class: s.title }, t(m.title)), h('p', { class: s.intro }, t(m.intro))),
    bench === null ? h('p', { class: `${s.notice} ${s.error}`, role: 'alert' }, t(m.noWebgl)) : null,
    h(
      'div',
      { class: s.layout },
      h('div', { class: s.column }, effectCard, kitCard, imagesCard),
      h('div', { class: s.column }, resultCard, recordCard),
    ),
  );
  host.append(page);

  // ------------------------------------------------------------------ controls
  function valueControl(u: BuiltinUniform): HTMLElement {
    const id = `fx-u-${u.name}`;
    const label = h('label', { class: s.label, for: id }, t(u.label.key, u.label.params));
    const set = (v: UniformValue): void => {
      state.values.set(u.name, v);
      invalidate();
    };
    const value = state.values.get(u.name) ?? u.default;
    if (u.type === 'vec4') {
      const rgba = typeof value === 'number' ? [value, value, value, 1] : [...value];
      const color = h('input', { id, class: s.color, type: 'color', value: toHex(rgba) });
      const alphaId = `${id}-a`;
      const alpha = h('input', {
        id: alphaId,
        class: s.number,
        type: 'number',
        min: 0,
        max: 1,
        step: 0.01,
        value: String(rgba[3] ?? 1),
      });
      const update = (): void => {
        const a = Number(alpha.value);
        set([...fromHex(color.value), Number.isFinite(a) ? a : 1]);
      };
      color.addEventListener('input', update);
      alpha.addEventListener('input', update);
      const alphaLabel = h('label', { class: s.label, for: alphaId }, t(m.alpha));
      return h('div', { class: s.row }, label, h('div', { class: s.controls }, color, alphaLabel, alpha));
    }
    if (u.type === 'vec2') {
      const xy = typeof value === 'number' ? [value, value] : [...value];
      const fields = [0, 1].map((k) =>
        h('input', {
          id: k === 0 ? id : `${id}-y`,
          class: s.number,
          type: 'number',
          step: 0.01,
          value: String(xy[k] ?? 0),
        }),
      );
      const update = (): void => {
        set(fields.map((f) => Number(f.value) || 0));
      };
      for (const f of fields) f.addEventListener('input', update);
      return h('div', { class: s.row }, label, h('div', { class: s.controls }, ...fields));
    }
    const num = typeof value === 'number' ? value : (value[0] ?? 0);
    const min = u.min ?? 0;
    const max = u.max ?? Math.max(1, num * 2);
    const step = u.integer === true ? 1 : (max - min) / 1000;
    const range = h('input', {
      class: s.range,
      type: 'range',
      min,
      max,
      step,
      value: String(num),
      'aria-label': t(u.label.key, u.label.params),
    });
    const field = h('input', {
      id,
      class: s.number,
      type: 'number',
      step: u.integer === true ? 1 : 'any',
      value: String(num),
    });
    range.addEventListener('input', () => {
      field.value = range.value;
      set(Number(range.value));
    });
    field.addEventListener('input', () => {
      const v = Number(field.value);
      if (field.value.trim() === '' || !Number.isFinite(v)) return;
      range.value = String(v);
      set(v);
    });
    return h('div', { class: s.row }, label, h('div', { class: s.controls }, range, field));
  }

  function syncEffect(): void {
    const e = state.effect;
    effectDesc.textContent =
      e === 'uvProbe' ? t(m.probeDesc) : t(BUILTIN_EFFECTS[e].description.key, BUILTIN_EFFECTS[e].description.params);
    const fidelity = e === 'uvProbe' ? 'asWritten' : builtinShaderFidelity(e);
    const status =
      fidelity === 'approximate'
        ? t(APPROXIMATE_BADGE.label.key)
        : fidelity === 'verified'
          ? t(fx.verified)
          : fidelity === 'asWritten'
            ? t(fx.asWritten)
            : t(fx.notPreviewed);
    badge.textContent = t(m.fidelity, { status });
    badge.title =
      fidelity === 'approximate'
        ? t(APPROXIMATE_BADGE.hint.key)
        : fidelity === 'verified'
          ? t(fx.verifiedHint)
          : fidelity === 'asWritten'
            ? t(fx.asWrittenHint)
            : t(fx.notPreviewedHint);
    badge.classList.toggle(s.badgeVerified, fidelity === 'verified' || fidelity === 'asWritten');
    valuesBox.replaceChildren(...uniformsOf(e).map(valueControl));
    // Glitch is the one built-in that reads the time: say how to match the Phira screenshot's moment.
    timeHint.textContent = e === 'glitch' ? t(m.glitchTimeHint, { song: KIT_TIMES.song }) : t(m.timeHint);
  }

  /** Scope buttons, and the HUD caveat of the chart-area scope (Phira draws its HUD after such an effect). */
  function syncScope(): void {
    for (const [scope, b] of scopeButtons) b.setAttribute('aria-pressed', String(scope === state.scope));
    scopeHud.classList.toggle(s.hidden, state.scope !== 'chart');
    if (state.scope === 'chart') scopeGroup.setAttribute('aria-describedby', 'fx-scope-hud');
    else scopeGroup.removeAttribute('aria-describedby');
  }

  /** Which panes show (view and whether a Phira screenshot is loaded). */
  function syncPanes(): void {
    for (const [view, b] of viewButtons) b.setAttribute('aria-pressed', String(view === state.view));
    const has = state.phira !== null;
    oursPane.classList.toggle(s.hidden, state.view !== 'side');
    phiraPane.classList.toggle(s.hidden, state.view !== 'side');
    diffPane.classList.toggle(s.hidden, state.view === 'blink');
    blinkPane.classList.toggle(s.hidden, state.view !== 'blink');
    phiraCanvas.classList.toggle(s.hidden, !has);
    phiraEmpty.classList.toggle(s.hidden, has);
    diffCanvas.classList.toggle(s.hidden, !has);
    diffEmpty.classList.toggle(s.hidden, has);
  }

  /** Applies a view change: panes, and the blink timer (restarted on the Phiedit picture). */
  function syncView(): void {
    syncPanes();
    if (blinkTimer !== null) clearInterval(blinkTimer);
    blinkTimer = null;
    if (state.view === 'blink') {
      state.blinkOurs = true;
      drawBlink();
      blinkTimer = setInterval(() => {
        state.blinkOurs = !state.blinkOurs;
        drawBlink();
      }, BLINK_MS);
    }
  }

  function drawBlink(): void {
    const showOurs = state.blinkOurs || state.phira === null;
    const img = showOurs ? state.ours : phiraForCompare();
    if (img !== null) paint(blinkCanvas, img);
    const name = t(showOurs ? m.paneOurs : m.panePhira);
    blinkCaption.textContent = t(m.blinkShowing, { name });
    blinkCanvas.setAttribute('aria-label', name);
  }

  let scaledPhira: {
    readonly from: Picture;
    readonly w: number;
    readonly h: number;
    readonly image: RgbaImage;
  } | null = null;
  /** The Phira screenshot at Before's size (scaled when the sizes differ). */
  function phiraForCompare(): RgbaImage | null {
    const p = state.phira;
    if (p === null) return null;
    const { width: w, height: hh } = state.before.image;
    if (p.image.width === w && p.image.height === hh) return p.image;
    if (scaledPhira?.from !== p || scaledPhira.w !== w || scaledPhira.h !== hh) {
      scaledPhira = { from: p, w, h: hh, image: scaleImage(p.image, w, hh) };
    }
    return scaledPhira.image;
  }

  const vars = (): Array<readonly [string, UniformValue]> => [...state.values.entries()];

  function notice(text: string, error = false): HTMLElement {
    return h('p', { class: error ? `${s.notice} ${s.error}` : s.notice }, text);
  }

  function render(): void {
    frame = 0;
    if (disposed) return;
    const before = state.before.image;
    const out: HTMLElement[] = [];
    beforeZone.name.textContent = t(m.imageSize, {
      name: state.before.name ?? t(m.testCard),
      w: before.width,
      h: before.height,
    });
    phiraZone.name.textContent =
      state.phira === null
        ? ''
        : t(m.imageSize, { name: state.phira.name ?? '', w: state.phira.image.width, h: state.phira.image.height });
    removePhira.disabled = state.phira === null;
    if (bench !== null) {
      if (state.beforeDirty) {
        bench.setInput(before);
        state.beforeDirty = false;
      }
      const r = bench.run({
        shader: shaderOf(state.effect),
        vars: vars(),
        time: state.time,
        viewport: state.scope === 'global' ? null : letterbox(before.width, before.height, CHART_ASPECT),
      });
      state.ours = r.image;
      paint(oursCanvas, r.image);
      for (const e of r.report.shaderErrors) out.push(notice(t(m.shaderError, { log: e.log }), true));
      if (r.report.notPreviewed.length > 0) out.push(notice(t(m.notPreviewed), true));
      if (r.report.droppedVars.length > 0) {
        out.push(notice(t(m.droppedVars, { names: r.report.droppedVars.map((d) => d.name).join(', ') })));
      }
    }
    const phira = phiraForCompare();
    const ours = state.ours;
    if (phira !== null && state.phira !== null) {
      paint(phiraCanvas, phira);
      if (phira !== state.phira.image) {
        out.push(
          notice(
            t(m.sizeMismatch, {
              w: state.phira.image.width,
              h: state.phira.image.height,
              bw: before.width,
              bh: before.height,
            }),
          ),
        );
      }
    }
    if (phira !== null && ours !== null) {
      const d = diffImages(ours, phira, { threshold: DEFAULT_THRESHOLD, ...heatColors(host) });
      state.stats = d.stats;
      paint(diffCanvas, d.heat);
      statValues.mean.textContent = t(m.levelValue, { value: d.stats.mean });
      statValues.psnr.textContent =
        d.stats.psnr === Infinity ? t(m.identical) : t(m.psnrValue, { value: d.stats.psnr });
      statValues.over.textContent = t(m.percentValue, { value: d.stats.over * 100 });
      statValues.max.textContent = t(m.levelValue, { value: d.stats.max });
    } else {
      state.stats = null;
      for (const v of Object.values(statValues)) v.textContent = '-';
    }
    notices.replaceChildren(...out);
    syncPanes();
    if (state.view === 'blink') drawBlink();
    syncRecord();
  }

  function schedule(): void {
    if (disposed || frame !== 0) return;
    frame = requestAnimationFrame(render);
  }

  /** A comparison input changed: redraw, and forget a verdict given for the previous inputs. */
  function invalidate(): void {
    state.verdict = null;
    schedule();
  }

  function currentRecord(): ReturnType<typeof signOffRecord> | null {
    if (state.verdict === null) return null;
    const p = state.phira;
    return signOffRecord({
      effect: state.effect,
      verdict: state.verdict,
      vars: vars(),
      time: state.time,
      scope: state.scope,
      capture: p === null ? null : { width: p.image.width, height: p.image.height },
      stats: state.stats,
      tester: tester.value,
      phira: phiraVersion.value,
      notes: notes.value,
    });
  }

  /** Evidence still missing before a SIGNED_OFF entry can be shown (names, translated). */
  function missingSignOffEvidence(): string[] {
    const missing: string[] = [];
    if (state.phira === null) missing.push(t(m.entryNeedCapture));
    if (tester.value.trim() === '') missing.push(t(m.entryNeedTester));
    if (phiraVersion.value.trim() === '') missing.push(t(m.entryNeedPhiraVersion));
    return missing;
  }

  function syncRecord(): void {
    matches.setAttribute('aria-pressed', String(state.verdict === 'matches'));
    differs.setAttribute('aria-pressed', String(state.verdict === 'differs'));
    const r = currentRecord();
    recordOut.classList.toggle(s.hidden, r === null);
    if (r === null) return;
    recordJson.textContent = signOffJson(r);
    // A ready-to-paste SIGNED_OFF entry only with full evidence: a Phira capture, a tester and the Phira version.
    const missing = missingSignOffEvidence();
    const entry = missing.length === 0 ? signOffEntry(r) : null;
    const wantsEntry = r.verdict === 'matches' && r.effect !== 'uvProbe';
    entryTitle.classList.toggle(s.hidden, entry === null);
    entryJson.classList.toggle(s.hidden, entry === null);
    entryJson.textContent =
      entry === null ? '' : `  ${entry.name}: ${JSON.stringify(entry.entry, null, 2).replace(/\n/g, '\n  ')},`;
    const showMissing = wantsEntry && entry === null;
    entryHint.classList.toggle(s.hidden, !showMissing);
    entryHint.textContent = showMissing ? t(m.entryIncomplete, { missing: missing.join(', ') }) : '';
  }

  // ------------------------------------------------------------------ events
  effectSelect.addEventListener('change', () => {
    state.effect = effectSelect.value as ComparedEffect;
    state.values = defaultsOf(state.effect);
    // Time belongs to Glitch and custom shaders only; drop a leftover value so it is not used or recorded elsewhere.
    if (!readsTime(state.effect)) {
      state.time = 0;
      timeInput.value = '0';
    }
    syncEffect();
    invalidate();
  });
  resetButton.addEventListener('click', () => {
    state.values = defaultsOf(state.effect);
    syncEffect();
    invalidate();
  });
  timeInput.addEventListener('input', () => {
    const v = Number(timeInput.value);
    if (timeInput.value.trim() === '' || !Number.isFinite(v)) return;
    state.time = v;
    invalidate();
  });
  kitButton.addEventListener('click', () => {
    kitButton.disabled = true;
    const effect = state.effect;
    void buildCaptureKit({
      shader: shaderOf(effect),
      vars: vars(),
      global: state.scope === 'global',
      name: t(m.kitName, { effect: labelOf(effect) }),
      tip: t(m.kitTip),
    })
      .then((bytes) => {
        if (!disposed) download(bytes, `phiedit-effect-${effect}.pez`, 'application/zip');
      })
      .finally(() => {
        kitButton.disabled = false;
      });
  });
  saveRender.addEventListener('click', () => {
    const img = state.ours;
    if (img === null) return;
    void encodePng(img).then((bytes) => {
      download(bytes, `phiedit-${state.effect}.png`, 'image/png');
    });
  });
  for (const [b, verdict] of [
    [matches, 'matches'],
    [differs, 'differs'],
  ] as const) {
    b.addEventListener('click', () => {
      state.verdict = verdict;
      syncRecord();
    });
  }
  for (const input of [tester, phiraVersion, notes]) input.addEventListener('input', syncRecord);
  copyButton.addEventListener('click', () => {
    const r = currentRecord();
    if (r === null) return;
    void navigator.clipboard.writeText(signOffJson(r)).then(() => {
      copyButton.textContent = t(m.copied);
      const id = setTimeout(() => {
        timers.delete(id);
        copyButton.textContent = t(m.copy);
      }, 1500);
      timers.add(id);
    });
  });
  jsonButton.addEventListener('click', () => {
    const r = currentRecord();
    if (r !== null) download(signOffJson(r), signOffFileName(r), 'application/json');
  });

  syncEffect();
  syncScope();
  syncView();
  schedule();

  return () => {
    disposed = true;
    if (frame !== 0) cancelAnimationFrame(frame);
    if (blinkTimer !== null) clearInterval(blinkTimer);
    for (const id of timers) clearTimeout(id);
    bench?.dispose();
    page.remove();
  };
}
